import { Card, CardContent } from "@/components/ui/library/card";
import SearchInput from "@/components/ui/searchInput";
import { useAppDispatch } from "@/stores/hook";
import {
  setProgramDetailId,
  setIsProgramDetails,
} from "@/features/academy/stores/academyFunctionalSlice";
import { GraduationCap, Layers, FileText, IndianRupee } from "lucide-react";
import { AdvancedSkeleton } from "@/components/feedback/loading/Skeletons";
import { ErrorBlock } from "@/components/feedback/error/AlertError";
import { EmptyState } from "@/components/feedback/empty/EmptyState";
import usePrograms from "@/features/academy/api/usePrograms";
import { useState } from "react";

const iconClass = "size-4 shrink-0 text-[#267355]";

export default function Program() {
  const dispatch = useAppDispatch();
  const { programs, isLoading, error, refetch } = usePrograms();
  const [search, setSearch] = useState("");

  const filtered = (programs ?? []).filter((p: any) =>
    (p.name ?? "").toLowerCase().includes(search.toLowerCase())
  );

  const handleProgramClick = (id: number | string) => {
    dispatch(setProgramDetailId(id));
    dispatch(setIsProgramDetails(true));
  };

  if (isLoading) {
    return (
      <div className="pt-2 pb-4">
        <AdvancedSkeleton variant="grid" count={4} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-6 w-full">
        <ErrorBlock
          message={(error as Error)?.message ?? "Failed to load programs"}
          retry={refetch}
        />
      </div>
    );
  }

  if (!programs?.length) {
    return (
      <div className="flex items-center justify-center py-6 w-full">
        <EmptyState
          title="No Programs Found"
          description="Create a new program to get started."
        />
      </div>
    );
  }

  return (
    <Card className="border-black-1 rounded-[16px] shadow-none">
      <CardContent className="px-4">
        <div className="flex flex-col gap-4">
          <SearchInput
            placeholder="Search programs..."
            isKbd={false}
            onChange={(e: any) => setSearch(e.target.value)}
          />
          <div>
            <span className="text-b2-mid">Programs</span>
            <span className="text-b2-mid text-[#697571]">
              {" "}
              ({filtered.length})
            </span>
          </div>
          <div className="flex flex-col gap-3">
            {filtered.map((program: any) => {
              const variantCount = Array.isArray(program.variants)
                ? program.variants.length
                : 0;

              const fees = (program.variants ?? [])
                .map((v: any) => v.courseFees)
                .filter((f: any) => f != null && !isNaN(f));
              const minFee = fees.length ? Math.min(...fees) : null;
              const maxFee = fees.length ? Math.max(...fees) : null;

              const feeLabel =
                minFee != null
                  ? minFee === maxFee
                    ? `${minFee.toLocaleString()}`
                    : `${minFee.toLocaleString()} – ${maxFee?.toLocaleString() ?? ""}`
                  : null;

              return (
                <div
                  key={program.id}
                  onClick={() => handleProgramClick(program.id)}
                  className="w-full rounded-[20px] border cursor-pointer transition-shadow hover:shadow-md overflow-hidden p-4 flex flex-col gap-2.5"
                  style={{
                    borderColor: "var(--color-green-2, #d6ebe3)",
                    background:
                      "linear-gradient(152deg, #EDFAF5 -11.48%, #FFF 52.35%)",
                    boxShadow: "0 4px 8px 0 rgba(30, 48, 42, 0.03)",
                  }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="size-9 shrink-0 rounded-full bg-[rgba(38,115,85,0.1)] flex items-center justify-center">
                      <GraduationCap className="size-4 text-[#051912]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#051912] truncate">
                        {program.name ?? "Unnamed Program"}
                      </p>
                      {program.code && (
                        <p className="text-xs text-[#697571] truncate">
                          {program.code}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-1 pl-0">
                    {program.description && (
                      <div className="flex items-start gap-2 min-w-0">
                        <FileText className={iconClass} />
                        <span className="text-xs text-[#697571] line-clamp-2 pt-0.5">
                          {program.description}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 min-w-0">
                      <Layers className={iconClass} />
                      <span className="text-xs text-[#697571]">
                        {variantCount} variant{variantCount !== 1 ? "s" : ""}
                      </span>
                    </div>
                    {feeLabel && (
                      <div className="flex items-center gap-2 min-w-0">
                        <IndianRupee className={iconClass} />
                        <span className="text-xs text-[#697571]">
                          {feeLabel}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
